import React from 'react';
import { usePrdStore } from '../store/prd-store';
import { exportToMarkdown } from '../utils';

export const EditorToolbar: React.FC = () => {
  const {
    document,
    isDirty,
    isSaving,
    activeView,
    config,
    saveDocument,
    publishDocument,
    setActiveView,
    createVersion,
  } = usePrdStore();

  if (!document) return null;

  const handleExport = async (format: 'markdown' | 'pdf' | 'docx' | 'html') => {
    if (config.onExport) {
      await config.onExport(format);
    } else if (format === 'markdown') {
      const markdown = exportToMarkdown(document);
      const blob = new Blob([markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${document.title}.md`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleCreateVersion = () => {
    const message = prompt('Enter version message:');
    if (message) {
      createVersion(message);
    }
  };

  return (
    <div className="prd-toolbar border-b bg-white dark:bg-gray-800 px-4 py-2">
      <div className="flex items-center justify-between">
        {/* Left section */}
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {document.title}
          </h1>
          
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>v{document.version}</span>
            <span>•</span>
            <span className={`capitalize ${
              document.status === 'published' ? 'text-green-600' : 
              document.status === 'approved' ? 'text-blue-600' : 
              'text-gray-600'
            }`}>
              {document.status}
            </span>
            {isDirty && (
              <>
                <span>•</span>
                <span className="text-orange-600">Unsaved changes</span>
              </>
            )}
          </div>
        </div>

        {/* Center section - View toggle */}
        <div className="flex items-center">
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setActiveView('editor')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                activeView === 'editor'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
              }`}
            >
              Editor
            </button>
            <button
              onClick={() => setActiveView('split')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                activeView === 'split'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
              }`}
            >
              Split
            </button>
            <button
              onClick={() => setActiveView('preview')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                activeView === 'preview'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
              }`}
            >
              Preview
            </button>
          </div>
        </div>

        {/* Right section - Actions */}
        <div className="flex items-center gap-2">
          {/* Version */}
          <button
            onClick={handleCreateVersion}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
            title="Create version"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>

          {/* Export dropdown */}
          <div className="relative group">
            <button className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100">
              Export
              <svg className="w-4 h-4 inline-block ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
              <button
                onClick={() => handleExport('markdown')}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Export as Markdown
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Export as PDF
              </button>
              <button
                onClick={() => handleExport('docx')}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Export as Word
              </button>
              <button
                onClick={() => handleExport('html')}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Export as HTML
              </button>
            </div>
          </div>

          {/* Save */}
          <button
            onClick={saveDocument}
            disabled={!isDirty || isSaving || !config.onSave}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              isDirty && !isSaving
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>

          {/* Publish */}
          <button
            onClick={publishDocument}
            disabled={isDirty || document.status === 'published' || !config.onPublish}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              !isDirty && document.status !== 'published'
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Publish
          </button>
        </div>
      </div>
    </div>
  );
};